import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search, Filter, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

/**
 * DataTable — tabela densa no desktop, cartoes no telemovel.
 *
 * Seccao 15 da especificacao 2.0. Antes havia so a tabela: num telemovel,
 * sete ou oito colunas obrigavam a arrastar de lado para ler uma linha, e a
 * ultima coluna (quase sempre as accoes) ficava fora de vista.
 *
 * Cada coluna pode declarar:
 *   - `esconderMobile: true` para nao aparecer no cartao;
 *   - `principal: true` para servir de titulo do cartao (por omissao, a
 *     primeira coluna).
 */
const DataTable = ({
  data = [],
  columns = [],
  title,
  searchable = true,
  pagination = true,
  pageSize = 10,
  pageSizeOptions = [5, 10, 25, 50],
  onRowClick,
  rowClassName,
  emptyMessage = 'Nenhum dado encontrado.',
  actions,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [itemsPerPage, setItemsPerPage] = useState(pageSize);

  // Filtrar dados
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    const lowerSearch = searchTerm.toLowerCase();
    return data.filter(row =>
      columns.some(col => {
        const value = col.accessor ? row[col.accessor] : row[col.key];
        if (value == null) return false;
        return String(value).toLowerCase().includes(lowerSearch);
      })
    );
  }, [data, searchTerm, columns]);

  // Ordenar dados
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = columns.find(c => c.key === sortConfig.key)?.accessor 
        ? a[columns.find(c => c.key === sortConfig.key).accessor] 
        : a[sortConfig.key];
      const bVal = columns.find(c => c.key === sortConfig.key)?.accessor 
        ? b[columns.find(c => c.key === sortConfig.key).accessor] 
        : b[sortConfig.key];

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, sortConfig, columns]);

  // Paginação
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(start, start + itemsPerPage);
  }, [sortedData, currentPage, itemsPerPage]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 text-gray-500" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 text-teal-400" />
      : <ArrowDown className="w-3 h-3 text-teal-400" />;
  };

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {title && <h3 className="text-lg font-semibold text-white">{title}</h3>}
        <div className="flex items-center gap-3 flex-wrap">
          {actions}
          {searchable && (
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Pesquisar..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="pl-9 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent w-full sm:w-64"
              />
            </div>
          )}
        </div>
      </div>

      {/* Tabela — desktop */}
      <div className="overflow-x-auto hidden md:block">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-700/50">
              {columns.map(col => (
                <th
                  key={col.key}
                  onClick={() => col.sortable !== false && handleSort(col.key)}
                  className={`px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider
                    ${col.sortable !== false ? 'cursor-pointer hover:text-white select-none' : ''}`}
                  style={{ width: col.width }}
                >
                  <div className="flex items-center gap-1 flex-wrap justify-center">
                    {col.label}
                    {col.sortable !== false && <SortIcon columnKey={col.key} />}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-gray-500">
                  <Filter className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>{emptyMessage}</p>
                </td>
              </tr>
            ) : (
              paginatedData.map((row, idx) => (
                <tr
                  key={row.id || idx}
                  onClick={() => onRowClick?.(row)}
                  className={`hover:bg-gray-700/30 transition-colors ${onRowClick ? 'cursor-pointer' : ''} ${rowClassName?.(row) || ''}`}
                >
                  {columns.map(col => (
                    <td key={col.key} className="px-4 py-3 text-sm text-gray-300 whitespace-nowrap">
                      {col.render ? col.render(row) : col.accessor ? row[col.accessor] : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Cartoes — telemovel */}
      <div className="md:hidden divide-y divide-gray-700">
        {paginatedData.length === 0 ? (
          <div className="px-4 py-12 text-center text-gray-500">
            <Filter className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p>{emptyMessage}</p>
          </div>
        ) : (
          paginatedData.map((row, idx) => {
            const visiveis = columns.filter((c) => !c.esconderMobile);
            const principal = visiveis.find((c) => c.principal) || visiveis[0];
            const restantes = visiveis.filter((c) => c !== principal);
            const valor = (col) => (col.render ? col.render(row) : col.accessor ? row[col.accessor] : row[col.key]);
            return (
              <div
                key={row.id || idx}
                onClick={() => onRowClick?.(row)}
                className={`p-4 ${onRowClick ? 'cursor-pointer active:bg-gray-700/40' : ''} ${rowClassName?.(row) || ''}`}
              >
                {principal && (
                  <div className="text-sm font-semibold text-white mb-2 break-words">{valor(principal)}</div>
                )}
                <dl className="grid grid-cols-[minmax(84px,auto)_minmax(0,1fr)] gap-x-3 gap-y-1.5">
                  {restantes.map((col) => (
                    <React.Fragment key={col.key}>
                      <dt className="text-[11px] uppercase tracking-wider text-gray-500 pt-0.5">{col.label}</dt>
                      <dd className="text-sm text-gray-300 break-words min-w-0">{valor(col)}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div className="p-4 border-t border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <span>Mostrar</span>
            <select
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
            >
              {pageSizeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <span>por página</span>
            <span className="ml-4">
              {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, sortedData.length)} de {sortedData.length}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="p-1 rounded hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed text-gray-400"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1 rounded hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed text-gray-400"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let page;
              if (totalPages <= 5) page = i + 1;
              else if (currentPage <= 3) page = i + 1;
              else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
              else page = currentPage - 2 + i;

              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 rounded text-sm font-medium transition-colors
                    ${currentPage === page 
                      ? 'bg-teal-500 text-white' 
                      : 'text-gray-400 hover:bg-gray-700 hover:text-white'}`}
                >
                  {page}
                </button>
              );
            })}

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-1 rounded hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed text-gray-400"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="p-1 rounded hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed text-gray-400"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
